import "./index.scss";

import { Form, Formik } from "formik";
import React, { useCallback, useState } from "react";
import { Feedback, feedback } from "/react4xp/shared/models/feedback";
import { useAppStore } from "/react4xp/shared/stores";
import { http, isRequiredField } from "/react4xp/shared/utils/client";

export default function Component() {
	const appStore = useAppStore((state) => state);

	const [data, setData] = useState<string>("");
	const [requestType, setRequestType] = useState<Record<string, string>[]>([]);
	const [systemType, setSystemType] = useState<Record<string, string>[]>([]);
	const [mptpType, setMptpType] = useState<Record<string, string>[]>([]);
	const [subjectType, setSubjectType] = useState<Record<string, string>[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async (values: Feedback) => {
		setIsSubmitting(true);

		try {
			const feedbackResponse = await http<string>({
				url: `${appStore.sitePath?.[0].link}/_/service/${appStore.appName}/feedback`,
				method: "PUT",
				body: JSON.stringify(values ?? "null"),
			});

			if (feedbackResponse.jsonData) {
				setData(feedbackResponse.jsonData);
			}
		} catch (e) {
			console.log(e);
		} finally {
			setIsSubmitting(false);
		}
	};

	const isFieldRequired = (field: string, values?: Feedback) => isRequiredField({ schema: feedback, field, values });

	const handleFileChange = (
		event: React.ChangeEvent<HTMLInputElement>,
		setFieldValue: (field: keyof Feedback, value: string) => void
	) => {
		const file = event.currentTarget.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onloadend = () => {
			const base64String = reader.result?.toString().split(",")[1] || "";
			setFieldValue("image", base64String);
			setFieldValue("fileName", file.name);
		};
		reader.readAsDataURL(file);
	};

	const fetchRequestType = useCallback(async () => {
		try {
			const requestTypeResponse = await http<{ data: Record<string, string>[] }>({
				url: `${appStore.sitePath?.[0].link}/_/service/${appStore.appName}/feedback`,
				method: "POST",
				body: JSON.stringify("feedback.request.type.selection" ?? "null"),
			});

			if (requestTypeResponse.jsonData?.data) {
				const formattedRequestTypes = requestTypeResponse.jsonData.data.map((item) => ({
					value: item.value,
					label: appStore.phrases?.[item.title],
				}));
				setRequestType(formattedRequestTypes);
			}
		} catch (e) {
			console.log(e);
		}
	}, [appStore.sitePath, appStore.appName]);

	const fetchSystemType = useCallback(async () => {
		try {
			const systemTypeResponse = await http<{ data: Record<string, string>[] }>({
				url: `${appStore.sitePath?.[0].link}/_/service/${appStore.appName}/feedback`,
				method: "POST",
				body: JSON.stringify("all.customs.systems.selection" ?? "null"),
			});

			if (systemTypeResponse.jsonData?.data) {
				setSystemType(systemTypeResponse.jsonData?.data);
			}
		} catch (e) {
			console.log(e);
		}
	}, [appStore.sitePath, appStore.appName]);

	const fetchMptpType = useCallback(async () => {
		try {
			const mptpTypeResponse = await http<{ data: Record<string, string>[] }>({
				url: `${appStore.sitePath?.[0].link}/_/service/${appStore.appName}/feedback`,
				method: "POST",
				body: JSON.stringify("all.mto.mptp.selection" ?? "null"),
			});

			if (mptpTypeResponse.jsonData?.data) {
				setMptpType(mptpTypeResponse.jsonData?.data);
			}
		} catch (e) {
			console.log(e);
		}
	}, [appStore.sitePath, appStore.appName]);

	const fetchSubjectType = useCallback(async () => {
		try {
			const subjectTypeResponse = await http<{ data: Record<string, string>[] }>({
				url: `${appStore.sitePath?.[0].link}/_/service/${appStore.appName}/feedback`,
				method: "POST",
				body: JSON.stringify("all.customs.subject.selection" ?? "null"),
			});

			if (subjectTypeResponse.jsonData?.data) {
				setSubjectType(subjectTypeResponse.jsonData?.data);
			}
		} catch (e) {
			console.log(e);
		}
	}, [appStore.sitePath, appStore.appName]);

	return (
		<div className="feedback-container">
			{data ? (
				<div className="thank-you-screen d-flex flex-column align-items-center justify-content-center my-5 fade-in">
					<h2 className="thank-you-title">
						{appStore.phrases?.["thankYou"]} {data}
					</h2>
					<p className="thank-you-message">{appStore.phrases?.["thankYouDescription"]}</p>
				</div>
			) : (
				<>
					<div className="feedback-header">
						<h2 className="feedback-title">{appStore.phrases?.["feedback"]}</h2>
						<p className="feedback-description">
							{appStore.phrases?.["feedbackDescription"]}
							<br />
							{appStore.phrases?.["feedbackSubtitle"]}
						</p>
					</div>
					<Formik
						initialValues={{
							name: "",
							contact: "",
							requestType: "",
							message: "",
							image: "",
							fileName: "",
							requestDateTime: "",
							consequence: "",
							mtoName: "",
							customsSystem: "",
							subjectError: "",
						}}
						validationSchema={feedback}
						onSubmit={handleSubmit}
					>
						{({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
							<Form className="feedback-form my-3">
								<div className="row g-3">
									<div className="col-12">
										<div className="form-group mb-2">
											<label
												className={`form-label fw-semibold ${isFieldRequired("name") ? "required" : ""}`}
												htmlFor="name"
											>
												{appStore.phrases?.["feedbackName"]}
											</label>
											<input
												name="name"
												id="name"
												className={`form-control ${touched.name && errors.name ? "is-invalid" : ""}`}
												value={values.name}
												onChange={handleChange}
												onBlur={handleBlur}
												placeholder={appStore.phrases?.["namePlaceholder"]}
											/>
											{touched.name && errors.name && (
												<div className="invalid-feedback">{appStore.phrases?.[errors.name]}</div>
											)}
										</div>
									</div>

									<div className="col-12">
										<div className="form-group mb-2">
											<label
												className={`form-label fw-semibold ${isFieldRequired("contact") ? "required" : ""}`}
												htmlFor="contact"
											>
												{appStore.phrases?.["contactDetails"]}
											</label>
											<input
												name="contact"
												id="contact"
												className={`form-control ${touched.contact && errors.contact ? "is-invalid" : ""}`}
												value={values.contact}
												onChange={handleChange}
												onBlur={handleBlur}
												placeholder={appStore.phrases?.["contactPlaceholder"]}
											/>
											{touched.contact && errors.contact && (
												<div className="invalid-feedback">{appStore.phrases?.[errors.contact]}</div>
											)}
										</div>
									</div>

									<div className="col-6">
										<div className="form-group mb-2">
											<label
												className={`form-label fw-semibold ${isFieldRequired("requestType") ? "required" : ""}`}
												htmlFor="requestType"
											>
												{appStore.phrases?.["typeOfAppeal"]}
											</label>
											<select
												name="requestType"
												id="requestType"
												className={`form-select ${touched.requestType && errors.requestType ? "is-invalid" : ""}`}
												value={values.requestType}
												onChange={handleChange}
												onBlur={handleBlur}
												onFocus={fetchRequestType}
											>
												<option value="">{appStore.phrases?.["selectValue"]}</option>
												{requestType.map((type) => (
													<option key={type.value} value={type.value}>
														{type.label}
													</option>
												))}
											</select>
											{touched.requestType && errors.requestType && (
												<div className="invalid-feedback">{appStore.phrases?.[errors.requestType]}</div>
											)}
										</div>
									</div>

									<div className="col-6">
										<div className="form-group mb-2">
											<label
												className={`form-label fw-semibold ${isFieldRequired("requestDateTime") || values.requestType === "error" ? "required" : ""}`}
												htmlFor="requestDateTime"
											>
												{appStore.phrases?.["dateTime"]}
											</label>
											<input
												name="requestDateTime"
												id="requestDateTime"
												className={`form-control ${touched.requestDateTime && errors.requestDateTime ? "is-invalid" : ""}`}
												value={values.requestDateTime}
												onChange={handleChange}
												onBlur={handleBlur}
											/>
											{touched.requestDateTime && errors.requestDateTime && (
												<div className="invalid-feedback">{appStore.phrases?.[errors.requestDateTime]}</div>
											)}
										</div>
									</div>

									{values.requestType === "error" && (
										<>
											<div className="col-4">
												<div className="form-group mb-2">
													<label
														className={`form-label fw-semibold ${isFieldRequired("mtoName") ? "required" : ""}`}
														htmlFor="mtoName"
													>
														МТО/МПТП
														{/*{appStore.phrases?.["typeOfAppeal"]}*/}
													</label>
													<select
														name="mtoName"
														id="mtoName"
														className={`form-select ${touched.mtoName && errors.mtoName ? "is-invalid" : ""}`}
														value={values.mtoName}
														onChange={handleChange}
														onBlur={handleBlur}
														onFocus={fetchMptpType}
													>
														<option value="">{appStore.phrases?.["selectValue"]}</option>
														{mptpType.map((type) => (
															<option key={type.value} value={type.value}>
																{type.title}
															</option>
														))}
													</select>
													{touched.mtoName && errors.mtoName && (
														<div className="invalid-feedback">{appStore.phrases?.[errors.mtoName]}</div>
													)}
												</div>
											</div>

											<div className="col-4">
												<div className="form-group mb-2">
													<label
														className={`form-label fw-semibold ${isFieldRequired("customsSystem") ? "required" : ""}`}
														htmlFor="customsSystem"
													>
														{appStore.phrases?.["System"]}
													</label>
													<select
														name="customsSystem"
														id="customsSystem"
														className={`form-select ${touched.customsSystem && errors.customsSystem ? "is-invalid" : ""}`}
														value={values.customsSystem}
														onChange={handleChange}
														onBlur={handleBlur}
														onFocus={fetchSystemType}
													>
														<option value="">{appStore.phrases?.["selectValue"]}</option>
														{systemType.map((type) => (
															<option key={type.value} value={type.value}>
																{type.title}
															</option>
														))}
													</select>
													{touched.customsSystem && errors.customsSystem && (
														<div className="invalid-feedback">{appStore.phrases?.[errors.customsSystem]}</div>
													)}
												</div>
											</div>

											<div className="col-4">
												<div className="form-group mb-2">
													<label
														className={`form-label fw-semibold ${isFieldRequired("subjectError") ? "required" : ""}`}
														htmlFor="subjectError"
													>
														{appStore.phrases?.["SubjectOfTheError"]}
													</label>
													<select
														name="subjectError"
														id="subjectError"
														className={`form-select ${touched.subjectError && errors.subjectError ? "is-invalid" : ""}`}
														value={values.subjectError}
														onChange={handleChange}
														onBlur={handleBlur}
														onFocus={fetchSubjectType}
													>
														<option value="">{appStore.phrases?.["selectValue"]}</option>
														{subjectType.map((type) => (
															<option key={type.value} value={type.value}>
																{type.title}
															</option>
														))}
													</select>
													{touched.subjectError && errors.subjectError && (
														<div className="invalid-feedback">{appStore.phrases?.[errors.subjectError]}</div>
													)}
												</div>
											</div>
										</>
									)}

									<div className="col-12">
										<div className="form-group mb-2">
											<label
												className={`form-label fw-semibold ${isFieldRequired("consequence") || values.requestType === "error" ? "required" : ""}`}
												htmlFor="consequence"
											>
												{appStore.phrases?.["consequenceLabel"]}
											</label>
											<textarea
												name="consequence"
												id="consequence"
												className={`form-control ${touched.consequence && errors.consequence ? "is-invalid" : ""}`}
												value={values.consequence}
												onChange={handleChange}
												onBlur={handleBlur}
												rows={3}
											/>
											{touched.consequence && errors.consequence && (
												<div className="invalid-feedback">{appStore.phrases?.[errors.consequence]}</div>
											)}
										</div>
									</div>

									<div className="col-12">
										<div className="form-group mb-2">
											<label
												className={`form-label fw-semibold ${isFieldRequired("message") ? "required" : ""}`}
												htmlFor="message"
											>
												{appStore.phrases?.["message"]}
											</label>
											<textarea
												name="message"
												id="message"
												className={`form-control ${touched.message && errors.message ? "is-invalid" : ""}`}
												value={values.message}
												onChange={handleChange}
												onBlur={handleBlur}
												rows={4}
												placeholder={appStore.phrases?.["messagePlaceholder"]}
											/>
											{touched.message && errors.message && (
												<div className="invalid-feedback">{appStore.phrases?.[errors.message]}</div>
											)}
										</div>
									</div>

									<div className="col-12">
										<div className="form-group mb-2">
											<label className="form-label fw-semibold" htmlFor="image">
												{appStore.phrases?.["image"]}
											</label>
											<input
												type="file"
												name="image"
												id="image"
												className="form-control"
												onChange={(event) => handleFileChange(event, setFieldValue)}
												onBlur={handleBlur}
												accept="image/*"
											/>
										</div>
									</div>
								</div>

								<div className="d-flex justify-content-center mt-4">
									<button className="btn btn-primary px-4 py-2" type="submit" disabled={isSubmitting}>
										{isSubmitting ? (
											<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
										) : (
											appStore.phrases?.["send"]
										)}
									</button>
								</div>
							</Form>
						)}
					</Formik>
				</>
			)}
		</div>
	);
}
